import { useCallback, useEffect, useRef, useState } from 'react'
import { useAppDispatch, useAppSelector } from '@/app/hooks'
import { selectUser, updateUser } from '@/features/auth/authSlice'
import type { UserData } from '@/types/auth'
import type { RoleSystemAnnouncement } from '../types/roleNotification'
import { SystemAnnouncementDetailModal } from '../components/SystemAnnouncementDetailModal'
import {
  markAnnouncementAcknowledgedInSession,
  shouldSuppressAnnouncementPopup,
} from '../utils/mergeNotificationShownFlags'
import { POPUP_DELAY_MS } from '../constants'

function asRoleAnnouncements(value: unknown): RoleSystemAnnouncement[] {
  if (!Array.isArray(value)) return []
  return value.filter(
    (item): item is RoleSystemAnnouncement =>
      Boolean(item) &&
      typeof item === 'object' &&
      typeof (item as RoleSystemAnnouncement).sa_id === 'number' &&
      typeof (item as RoleSystemAnnouncement).announcement_topic === 'string',
  )
}

/**
 * Angular dashboard / application-list behaviour:
 * wait 2s, then open a System Announcement dialog for each role notification
 * that has not yet been shown this session (`!isshown`). Mark `isshown = '1'`
 * when opening and persist on the selected role (localStorage `user`).
 *
 * "Okay, I've Got It" POSTs `/api/notification/{id}` then dismisses — same as
 * Angular. The item stays on the role with `isshown='1'` so verify/reload cannot
 * reopen it. Session ack is an extra guard if the API re-sends the item.
 */
export function useSystemAnnouncementPopup() {
  const user = useAppSelector(selectUser)
  const dispatch = useAppDispatch()
  const [current, setCurrent] = useState<RoleSystemAnnouncement | null>(null)
  const queueRef = useRef<RoleSystemAnnouncement[]>([])
  const startedForRoleRef = useRef<string | null>(null)
  const userRef = useRef(user)
  userRef.current = user

  const persistNotifications = useCallback(
    (notifications: RoleSystemAnnouncement[]) => {
      const latest = userRef.current
      if (!latest?.current_role) return
      const updatedUser: UserData = {
        ...latest,
        current_role: {
          ...latest.current_role,
          notification: notifications,
        },
      }
      // updateUser merges isshown + persists merged user to localStorage.
      dispatch(updateUser(updatedUser))
    },
    [dispatch],
  )

  const roleId = user?.current_role?.role_id
  const roleName = user?.current_role?.role_name
  const hasNotifications = asRoleAnnouncements(user?.current_role?.notification).length > 0

  useEffect(() => {
    if (roleId == null || !roleName || !hasNotifications) return

    const roleKey = `${roleId}:${roleName}`
    if (startedForRoleRef.current === roleKey) return

    let cancelled = false
    const timer = window.setTimeout(() => {
      if (cancelled) return

      const latest = userRef.current
      const notifications = asRoleAnnouncements(latest?.current_role?.notification)
      if (!notifications.length) return

      startedForRoleRef.current = roleKey

      const pending = notifications.filter(
        (item) => !shouldSuppressAnnouncementPopup(item),
      )
      // Angular: mark isshown='1' for every item opened in this pass, then persist.
      const updated = notifications.map((item) =>
        pending.some((p) => p.sa_id === item.sa_id)
          ? { ...item, isshown: '1' as const }
          : item,
      )

      persistNotifications(updated)

      if (!pending.length) return
      setCurrent(pending[0] ?? null)
      queueRef.current = pending.slice(1)
    }, POPUP_DELAY_MS)

    return () => {
      cancelled = true
      window.clearTimeout(timer)
    }
  }, [roleId, roleName, hasNotifications, persistNotifications])

  const dismiss = useCallback(() => {
    const [next, ...rest] = queueRef.current
    queueRef.current = rest
    setCurrent(next ?? null)
  }, [])

  /**
   * After successful POST /api/notification/{id} — Angular only dismisses.
   * Keep the item with isshown='1' (already set on open) so localStorage still
   * suppresses on reload; session ack covers verify re-delivery edge cases.
   */
  const handleAcknowledged = useCallback(
    (saId: number) => {
      markAnnouncementAcknowledgedInSession(saId)
      const latest = userRef.current
      const notifications = asRoleAnnouncements(latest?.current_role?.notification).map(
        (item) =>
          item.sa_id === saId ? { ...item, isshown: '1' as const } : item,
      )
      persistNotifications(notifications)
      queueRef.current = queueRef.current.filter((item) => item.sa_id !== saId)
      const [next, ...rest] = queueRef.current
      queueRef.current = rest
      setCurrent(next ?? null)
    },
    [persistNotifications],
  )

  const modal = (
    <SystemAnnouncementDetailModal
      isOpen={Boolean(current)}
      announcement={current}
      onDismiss={dismiss}
      onAcknowledged={handleAcknowledged}
    />
  )

  return { modal }
}
